import React, { useState, useRef } from 'react';
import PDFViewer, { PDFViewerHandle } from './PDFViewer';
import DocViewer, { DocViewerHandle } from './DocViewer';

interface SourceViewerProps {
  source: {
    id: string;
    title: string;
    page: number;
    content: string;
    type: 'pdf' | 'doc' | 'text';
    url?: string; // Add URL field for actual document sources
  };
  isOpen: boolean;
  onClose: () => void;
}

const SourceViewer: React.FC<SourceViewerProps> = ({ source, isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(source.page || 1);

  // References to document viewers to control them
  const pdfViewerRef = useRef<PDFViewerHandle>(null);
  const docViewerRef = useRef<DocViewerHandle>(null);

  // Use the source URL if provided
  const documentUrl = source.url || '';

  // Debug log source and URL
  console.log('SourceViewer: Source data:', source);
  console.log('SourceViewer: Document URL:', documentUrl);

  // Determine document type based on URL extension, regardless of what's in the source.type
  const getDocumentType = (url: string): 'pdf' | 'doc' | 'text' => {
    if (!url) return 'text';

    const lowerUrl = url.toLowerCase();
    if (lowerUrl.endsWith('.pdf')) return 'pdf';
    if (lowerUrl.endsWith('.doc') || lowerUrl.endsWith('.docx')) return 'doc';

    // If we can't determine from URL, fall back to the source type
    return source.type;
  };

  const documentType = getDocumentType(documentUrl);

  const handlePreviousPage = () => {
    if (documentType === 'pdf' && pdfViewerRef.current) {
      pdfViewerRef.current.previousPage();
      setCurrentPage(pdfViewerRef.current.getCurrentPage());
    } else if (documentType === 'doc' && docViewerRef.current) {
      docViewerRef.current.previousPage();
      setCurrentPage(docViewerRef.current.getCurrentPage());
    } else {
      setCurrentPage(prev => Math.max(1, prev - 1));
    }
  };

  const handleNextPage = () => {
    if (documentType === 'pdf' && pdfViewerRef.current) {
      pdfViewerRef.current.nextPage();
      setCurrentPage(pdfViewerRef.current.getCurrentPage());
    } else if (documentType === 'doc' && docViewerRef.current) {
      docViewerRef.current.nextPage();
      setCurrentPage(docViewerRef.current.getCurrentPage());
    } else {
      setCurrentPage(prev => prev + 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative w-full max-w-4xl h-[90vh] bg-secondary rounded-lg shadow-xl flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center">
            <div className="mr-3 p-2 rounded-full bg-accent/20">
              {documentType === 'pdf' ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-accent">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              ) : documentType === 'doc' ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-accent">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-accent">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white truncate max-w-md">{source.title}</h3>
              <p className="text-sm text-gray-400">Page {source.page}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Document Viewer */}
        <div className="flex-1 flex-grow overflow-hidden px-6 bg-gray-800" >
          {documentType === 'pdf' && documentUrl ? (
            <PDFViewer
              url={documentUrl}
              pageNumber={source.page}
              ref={pdfViewerRef}
            />
          ) : documentType === 'doc' && documentUrl ? (
            <DocViewer
              url={documentUrl}
              pageNumber={currentPage}
              citedPage={source.page}
              ref={docViewerRef}
            />
          ) : (
            // Fallback if URL is missing or for text type
            <div className="bg-white rounded-lg px-8 shadow-lg max-w-3xl mx-auto">
              <div className="border-l-4 border-accent pl-4 mb-6">
                <p className="text-gray-800">{source.content}</p>
              </div>

              {error ? (
                <div className="bg-red-100 border-l-4 border-red-500 p-4 my-4">
                  <p className="text-red-700">Error loading document: {error}</p>
                </div>
              ) : null}

              {!documentUrl ? (
                <div className="prose prose-sm text-gray-700 mx-auto mt-8">
                  <h2 className="text-xl font-bold">Document Unavailable</h2>
                  <p>The document URL is not available or has expired.</p>
                </div>
              ) : (
                <div className="prose prose-sm text-gray-700 mx-auto mt-8">
                  <h2 className="text-xl font-bold">Loading Document...</h2>
                </div>
              )}

              {/* {source.url && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-accent text-white py-2 px-4 rounded-md hover:bg-accent/80 transition-colors inline-block"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Download Document
                  </a>
                </div>
              )} */}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-4 flex justify-between items-center">
          {/* <div className="flex gap-2">
            {documentType !== 'text' && (
              <>
                <button
                  onClick={handlePreviousPage}
                  className="bg-gray-700 hover:bg-gray-600 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                  Previous Page
                </button>
                <button
                  onClick={handleNextPage}
                  className="bg-gray-700 hover:bg-gray-600 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1"
                >
                  Next Page
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </>
            )}
          </div> */}
          <div>
            <span className="text-sm text-gray-400">Cited in response to your query</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourceViewer;
